export function captureLiveHistoryPosition(
  history: HTMLElement,
  banner: HTMLElement
) {
  return {
    history: history.getBoundingClientRect(),
    messagesBottom: history.firstElementChild!.getBoundingClientRect().bottom,
    footerTop: history.nextElementSibling!.getBoundingClientRect().top,
    bannerTop: banner.getBoundingClientRect().top,
  };
}

type LiveHistoryReturnOptions = {
  scroll: HTMLElement;
  content: HTMLElement;
  history: HTMLElement;
  banner: HTMLElement;
  transcript: HTMLElement | null;
  onFinish: () => void;
  from?: ReturnType<typeof captureLiveHistoryPosition>;
};

export function startLiveHistoryReturn({
  scroll,
  content,
  history,
  banner,
  transcript,
  onFinish,
  from = captureLiveHistoryPosition(history, banner),
}: LiveHistoryReturnOptions) {
  const surface = history.parentElement!;
  const footer = history.nextElementSibling as HTMLElement;
  const messages = history.firstElementChild as HTMLElement;
  const animations: Animation[] = [];
  const surfaceStyle = surface.getAttribute("style");
  const historyStyle = history.getAttribute("style");
  const scrollTop = history.scrollTop;
  const bottomGap = Math.max(
    0,
    history.scrollHeight - history.clientHeight - scrollTop
  );
  let active = true;

  function finish() {
    if (!active) return;
    active = false;
    onFinish();
  }

  function blockScroll(event: Event) {
    if (event instanceof WheelEvent && event.ctrlKey) return;
    if (event.cancelable) event.preventDefault();
  }

  function dispose() {
    active = false;
    scroll.removeEventListener("wheel", blockScroll);
    scroll.removeEventListener("touchmove", blockScroll);
    for (const animation of animations) {
      animation.onfinish = null;
      animation.cancel();
    }
    if (surfaceStyle === null) surface.removeAttribute("style");
    else surface.setAttribute("style", surfaceStyle);
    if (historyStyle === null) history.removeAttribute("style");
    else history.setAttribute("style", historyStyle);
    history.scrollTop = scrollTop;
  }

  if (
    !content.animate ||
    !scroll.clientHeight ||
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  ) {
    finish();
    return dispose;
  }

  surface.style.flex = "none";
  surface.style.height = `${scroll.clientHeight + footer.offsetHeight}px`;
  history.style.overflowY = "hidden";
  const finalScrollTop = Math.max(
    0,
    history.scrollHeight - history.clientHeight
  );
  history.scrollTop = Math.max(0, finalScrollTop - bottomGap);
  const to = history.getBoundingClientRect();
  const start = from.messagesBottom - messages.getBoundingClientRect().bottom;
  const end = scroll.getBoundingClientRect().bottom - to.bottom;
  const timing = {
    duration: 620,
    easing: "cubic-bezier(0.22, 0.8, 0.2, 1)",
    fill: "both" as const,
  };

  function animate(element: HTMLElement, frames: Keyframe[], duration = 620) {
    const animation = element.animate(frames, { ...timing, duration });
    animations.push(animation);
    return animation;
  }

  // Resizing first prevents a viewport-height jump when the live feed replaces this surface.
  const movement = animate(content, [
    { transform: `translate3d(0, ${start}px, 0)` },
    { transform: `translate3d(0, ${end}px, 0)` },
  ]);
  const messageMovement = animate(messages, [
    { transform: "translateY(0px)" },
    { transform: `translateY(${history.scrollTop - finalScrollTop}px)` },
  ]);
  animate(history, [
    {
      clipPath: `inset(${from.history.top - to.top - start}px 0 ${to.bottom + start - from.history.bottom}px 0)`,
    },
    { clipPath: "inset(0px 0px 0px 0px)" },
  ]);
  const surfaceAppearance = getComputedStyle(surface);
  animate(surface, [
    {
      backgroundColor: surfaceAppearance.backgroundColor,
      outlineColor: surfaceAppearance.outlineColor,
    },
    { backgroundColor: "transparent", outlineColor: "transparent" },
  ]);
  for (const [element, top] of [
    [footer, from.footerTop],
    [banner, from.bannerTop],
  ] as const) {
    const offset = top - element.getBoundingClientRect().top;
    animate(
      element,
      [
        { opacity: 1, transform: `translateY(${offset}px)` },
        { opacity: 0, transform: `translateY(${offset + 12}px)` },
      ],
      160
    );
  }
  if (transcript)
    animate(
      transcript,
      [
        { opacity: 1, transform: "translateY(0px)" },
        { opacity: 0, transform: "translateY(16px)" },
      ],
      160
    );
  scroll.addEventListener("wheel", blockScroll, { passive: false });
  scroll.addEventListener("touchmove", blockScroll, { passive: false });
  movement.onfinish = () => {
    history.scrollTop = finalScrollTop;
    messageMovement.cancel();
    finish();
  };
  return dispose;
}
